import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Calculator,
  LayoutDashboard,
  MessageSquareText,
  Briefcase,
  Users,
  Menu,
  TrendingUp,
  Inbox,
  CheckCircle2,
  Trash2,
  Plus,
  X,
  FileText,
  ScrollText,
  Send,
  Download,
  Copy,
  Settings,
  Search,
  Image as ImageIcon,
  Edit3,
  ClipboardList,
  ShieldAlert,
  LayoutGrid,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";
import { api, formatApiError, isApiNetworkError } from "@/lib/api";
import DateField, { toDateInputValue } from "@/components/DateField";
import QuoteChat from "@/components/QuoteChat";
import NotificationBell, { refreshAdminNotifications } from "@/components/NotificationBell";
import CmsTab from "@/pages/admin/CmsTab";
import SeoAeoGeoTab from "@/pages/admin/SeoAeoGeoTab";
import MediaTab from "@/pages/admin/MediaTab";
import PortfolioCmsTab from "@/pages/admin/PortfolioCmsTab";
import SalesCalculatorV2 from "@/pages/admin/SalesCalculatorV2";
import SalesPricesTab from "@/pages/admin/SalesPricesTab";
import QuoteNoticesTab from "@/pages/admin/QuoteNoticesTab";
import QuoteNoticeEditor, { normalizeNoticeRows } from "@/pages/admin/QuoteNoticeEditor";
import ContractClausesTab from "@/pages/admin/ContractClausesTab";
import ContractClauseEditor, {
  ContractSpecialEditor,
  normalizeClauseRows,
  normalizeSpecialRows,
} from "@/pages/admin/ContractClauseEditor";
import FieldConsultation from "@/pages/admin/FieldConsultation";
import DeleteRequestsTab from "@/pages/admin/DeleteRequestsTab";
import DeleteRequestModal from "@/pages/admin/DeleteRequestModal";
import BatchDeleteModal from "@/pages/admin/BatchDeleteModal";
import SalesBulkBar, { toggleId } from "@/pages/admin/SalesBulkBar";
import BackendServiceNotice from "@/components/BackendServiceNotice";
import { useAuth } from "@/lib/auth";
import { useCms } from "@/lib/cms";
import { BACKEND_BASE } from "@/lib/backend";
import { QUOTE_ITEM_TEMPLATES, quoteItemFromTemplate } from "@/data/quoteItemTemplates";
import AdminUsersTab from "@/pages/admin/AdminUsersTab";
import { resolveAdminNotificationTarget } from "@/lib/adminNotificationTarget";
import AdminSidebar from "@/pages/admin/AdminSidebar";
import {
  AdminEmptyState,
  AdminPageHeader,
  AdminPagination,
  paginateItems,
} from "@/pages/admin/AdminUx";
import QuoteTechnicalReview from "@/pages/admin/QuoteTechnicalReview";
import QuoteLifecycleBadges from "@/pages/admin/QuoteLifecycleBadges";
import { filenameFromDisposition, formatSignedWon, formatWon } from "@/pages/admin/quoteLifecycle";
import "./AdminDashboard.css";

const TABS_ALL = [
  { id: "overview", label: "개요", icon: LayoutDashboard, super: false },
  { id: "inquiries", label: "문의 관리", icon: MessageSquareText, super: false },
  { id: "quotes", label: "견적 관리", icon: FileText, super: false },
  { id: "quote-notices", label: "견적 안내문", icon: FileText, super: true },
  { id: "field-consultation", label: "현장 상담 가이드", icon: ClipboardList, super: false },
  { id: "sales-calculator", label: "영업 견적 계산기", icon: Calculator, super: false },
  { id: "sales-prices", label: "영업 단가 관리", icon: Calculator, super: true },
  { id: "contracts", label: "계약 관리", icon: ScrollText, super: false },
  { id: "contract-clauses", label: "계약 조항 관리", icon: ScrollText, super: true },
  { id: "delete-requests", label: "삭제 요청", icon: ShieldAlert, super: true },
  { id: "projects", label: "프로젝트", icon: Briefcase, super: false },
  { id: "customers", label: "고객 CRM", icon: Users, super: false },
  { id: "users", label: "권한 관리", icon: Users, super: true },
  { id: "cms", label: "홈페이지 콘텐츠", icon: FileText, super: true },
  { id: "media", label: "미디어 관리", icon: ImageIcon, super: true },
  { id: "portfolio", label: "포트폴리오 관리", icon: LayoutGrid, super: true },
  { id: "site", label: "사이트 정보", icon: Settings, super: true },
  { id: "seo", label: "SEO · AEO · GEO", icon: Search, super: true },
];
const TABS = TABS_ALL;

function salesTabIds(user) {
  const ids = ["field-consultation", "sales-calculator", "quotes"];
  if (user?.sales_level === "sales_super_admin") ids.push("contracts");
  return ids;
}

function AccessDenied() {
  return (
    <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-10 text-center" data-testid="admin-access-denied">
      <h3 className="font-display text-lg mb-2">접근 권한이 없습니다</h3>
      <p className="text-sm text-white/50">현재 계정에는 해당 메뉴의 접근 권한이 없습니다.</p>
    </div>
  );
}

function canSeeTab(tab, user) {
  if (tab.super && user?.role !== "super_admin") return false;
  if (user?.department === "sales") return salesTabIds(user).includes(tab.id);
  return true;
}

const INQUIRY_STATUS_LABEL = {
  received: "문의",
  responding: "상담중",
  answered: "상담완료",
  new: "문의",
  contacted: "상담중",
  qualified: "상담중",
  won: "상담완료",
  lost: "상담완료",
};

const QUOTE_STATUS_LABEL = {
  draft: "작성중",
  sent: "발송완료",
  viewed: "열람완료",
  approved: "승인",
  revision_requested: "수정요청",
  rejected: "반려",
};

const CONTRACT_STATUS_LABEL = QUOTE_STATUS_LABEL;

const PROVISIONAL_QUOTE_NOTICE = "본 견적은 상담 단계의 임시 견적이며, 내부 기술 검토 및 최종 요구사항 확인 후 금액이 조정될 수 있습니다.";
const CUSTOM_CONSULT_NOTICE = "완전 맞춤형 구축은 요구사항에 따라 개발 범위 차이가 커 별도 상담 후 최종 견적이 확정됩니다.";

const PROJECT_STATUS_LABEL = {
  planning: "기획",
  in_progress: "개발 중",
  testing: "테스트",
  live: "오픈",
  maintenance: "유지보수",
};

function contractCreateErrorMessage(err) {
  const detail = err?.response?.data?.detail;
  const locs = Array.isArray(detail)
    ? detail.flatMap((item) => item?.loc || []).map((part) => String(part))
    : [];
  const text = [
    typeof detail === "string" ? detail : "",
    locs.join(" "),
  ].join(" ").toLowerCase();
  if (text.includes("customer_email") || text.includes("customer_id") || text.includes("email")) {
    return "계약서 생성에 실패했습니다: 고객 정보를 확인해주세요.";
  }
  if (text.includes("contract_amount") || text.includes("amount")) {
    return "계약서 생성에 실패했습니다: 계약금액을 확인해주세요.";
  }
  if (err?.response?.status === 422) {
    return "계약서 생성에 실패했습니다: 필수 입력값을 확인해주세요.";
  }
  return "계약서 생성에 실패했습니다. 입력 내용을 확인해주세요.";
}

function contractDeleteErrorMessage(err) {
  const status = err?.response?.status;
  if (status === 403) return "계약서를 삭제할 권한이 없습니다.";
  if (status === 404) return "계약서를 찾을 수 없습니다.";
  if (status === 409) return "연결된 데이터가 있어 계약서를 삭제할 수 없습니다.";
  return "계약서 삭제에 실패했습니다.";
}

function formatInquiryYn(value) {
  if (value === true || value === "true") return "예";
  if (value === false || value === "false") return "아니오";
  return null;
}

function resolveUploadUrl(url) {
  if (!url) return "#";
  if (url.startsWith("http")) return url;
  return `${BACKEND_BASE}${url}`;
}

function InquiryFormDetail({ inquiry }) {
  const sections = [
    {
      title: "기본 정보",
      rows: [
        ["회사명", inquiry.company],
        ["담당자명", inquiry.name],
        ["이메일", inquiry.email],
        ["연락처", inquiry.phone],
      ],
    },
    {
      title: "프로젝트 정보",
      rows: [
        ["서비스 유형", inquiry.service_type],
        ["제작 목적", inquiry.project_purpose],
        ["예산", inquiry.budget],
        ["희망 일정", inquiry.timeline],
      ],
    },
    {
      title: "인프라 / 기존 사이트",
      rows: [
        ["도메인 보유", formatInquiryYn(inquiry.has_domain)],
        ["호스팅 보유", formatInquiryYn(inquiry.has_hosting)],
        ["기존 사이트 보유", formatInquiryYn(inquiry.has_existing_site)],
        ["기존 사이트 URL", inquiry.existing_site_url],
      ],
    },
    {
      title: "상세 내용",
      rows: [
        ["상세 요구사항", inquiry.requirements],
        ["문의 내용", inquiry.message],
      ],
    },
  ];

  return (
    <div className="mt-4 space-y-4 border-t border-white/10 pt-4">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {sections.map((section) => (
          <div key={section.title} className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wider text-white/40 mb-3">{section.title}</div>
            <dl className="space-y-2">
              {section.rows
                .filter(([, value]) => value != null && value !== "")
                .map(([label, value]) => (
                  <div key={label} className="grid grid-cols-[110px_1fr] gap-3 text-sm">
                    <dt className="text-white/45 shrink-0">{label}</dt>
                    <dd className="text-white/85 whitespace-pre-wrap break-words">{value}</dd>
                  </div>
                ))}
            </dl>
          </div>
        ))}
      </div>
      {inquiry.attachments?.length > 0 && (
        <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4">
          <div className="text-[11px] uppercase tracking-wider text-white/40 mb-3">첨부 파일</div>
          <div className="flex flex-wrap gap-2">
            {inquiry.attachments.map((a, i) => (
              <a
                key={i}
                href={resolveUploadUrl(a.url)}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-[#0055FF] border border-[#0055FF]/20 bg-[#0055FF]/10 px-3 py-1.5 rounded-full hover:bg-[#0055FF]/20"
              >
                {a.filename}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user: authUser, role: authRole, logout: authLogout } = useAuth();
  const { cms: cmsForLogo, refresh: refreshCms } = useCms();
  const [tab, setTab] = useState(() => searchParams.get("tab") || "overview");
  const [focusQuoteId, setFocusQuoteId] = useState(() => searchParams.get("quote") || null);
  const [focusContractId, setFocusContractId] = useState(() => searchParams.get("contract") || null);
  const [contractPrefillQuote, setContractPrefillQuote] = useState(null);
  const [stats, setStats] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const tabsNavRef = useRef(null);

  const adminRole = authRole || "super_admin";
  const isSalesAccount = authUser?.department === "sales";

  const logout = () => {
    authLogout();
    navigate("/admin/login", { replace: true });
  };

  const fetchAll = async () => {
    if (isSalesAccount) {
      setStats(null);
      setInquiries([]);
      setProjects([]);
      setCustomers([]);
      return;
    }
    try {
      const [s, i, p, c] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/inquiries"),
        api.get("/admin/projects"),
        api.get("/admin/customers"),
      ]);
      setStats(s.data);
      setInquiries(i.data);
      setProjects(p.data);
      setCustomers(c.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
      } else {
        toast.error(formatApiError(err, "데이터 로드 실패"));
      }
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSalesAccount]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    html.classList.add("admin-lock-scroll");
    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      html.classList.remove("admin-lock-scroll");
    };
  }, []);

  useEffect(() => {
    if (isSalesAccount && !salesTabIds(authUser).includes(tab)) {
      switchTab("field-consultation");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSalesAccount, tab]);

  const switchTab = (nextTab) => {
    setTab(nextTab);
    setSidebarOpen(false);
    if (nextTab === "overview") {
      setSearchParams({});
    } else if (nextTab === "quotes" && focusQuoteId) {
      setSearchParams({ tab: nextTab, quote: focusQuoteId });
    } else {
      setSearchParams({ tab: nextTab });
    }
  };

  const handleQuoteConverted = (quote) => {
    const qid = quote?.id;
    setTab("quotes");
    if (qid) {
      setFocusQuoteId(qid);
      setSearchParams({ tab: "quotes", quote: qid });
    } else {
      setSearchParams({ tab: "quotes" });
    }
  };

  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab && urlTab !== tab) {
      setTab(urlTab);
    }
    const quoteId = searchParams.get("quote");
    if (quoteId) setFocusQuoteId(quoteId);
    const contractId = searchParams.get("contract");
    if (contractId) setFocusContractId(contractId);
  }, [searchParams, tab]);

  useEffect(() => {
    const nav = tabsNavRef.current;
    if (!nav) return;
    const active = nav.querySelector(`[data-testid="admin-tab-${tab}"]`);
    if (!active) return;
    const navRect = nav.getBoundingClientRect();
    const elRect = active.getBoundingClientRect();
    const pad = 12;
    if (elRect.top < navRect.top + pad) {
      nav.scrollBy({ top: elRect.top - navRect.top - pad, behavior: "smooth" });
    } else if (elRect.bottom > navRect.bottom - pad) {
      nav.scrollBy({ top: elRect.bottom - navRect.bottom + pad, behavior: "smooth" });
    }
  }, [tab]);

  const newInquiryCount = inquiries.filter(
    (q) => q.status === "received" || q.status === "new"
  ).length;

  const handleNotificationClick = (notification) => {
    const t = resolveAdminNotificationTarget(notification);
    if (!t.tab) {
      if ((notification?.link || "").startsWith("/admin")) navigate(notification.link);
      return;
    }
    setTab(t.tab);
    const params = { tab: t.tab };
    if (t.tab === "quotes" && t.id) {
      setFocusQuoteId(t.id);
      params.quote = t.id;
    } else if (t.tab === "delete-requests" && t.id) {
      params.request = t.id;
    } else if (t.tab === "field-consultation" && t.id) {
      params.consult = t.id;
    } else if (t.tab === "contracts" && t.id) {
      setFocusContractId(t.id);
      params.contract = t.id;
    }
    setSearchParams(params);
  };

  return (
    <div
      data-testid="admin-dashboard"
      className={`admin-shell${sidebarOpen ? " admin-shell--nav-open" : ""}`}
    >
      <button
        type="button"
        className="admin-shell__overlay"
        aria-label="메뉴 닫기"
        onClick={() => setSidebarOpen(false)}
      />
      <AdminSidebar
        tabs={TABS.filter((t) => canSeeTab(t, authUser))}
        activeTab={tab}
        onSelect={switchTab}
        user={authUser}
        cms={cmsForLogo}
        onLogout={logout}
        newInquiryCount={newInquiryCount}
        navRef={tabsNavRef}
        notification={(
          <NotificationBell
            scope="admin"
            onItemClick={handleNotificationClick}
            panelPlacement="aside"
          />
        )}
      />
      <div className="admin-main">
        <header className="admin-dashboard-header" data-testid="admin-dashboard-header">
          <button
            type="button"
            className="admin-dashboard-header__menu"
            data-testid="admin-sidebar-toggle"
            aria-label="메뉴 열기"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu size={16} aria-hidden="true" />
          </button>
          <h1 className="admin-dashboard-header__title">
            {TABS.find((t) => t.id === tab)?.label || "관리자"}
          </h1>
        </header>
        <div className="admin-main__body">
        <BackendServiceNotice className="mb-6" />
        {isSalesAccount && !canSeeTab({ id: tab, super: false }, authUser) && <AccessDenied />}
        {tab === "overview" && !isSalesAccount && (
          <Overview
            stats={stats}
            inquiries={inquiries}
            onOpenInquiries={() => switchTab("inquiries")}
            onOpenQuotes={() => switchTab("quotes")}
            onOpenContracts={() => switchTab("contracts")}
          />
        )}
        {tab === "inquiries" && (isSalesAccount ? <AccessDenied /> : (
          <InquiriesTab data={inquiries} onChange={fetchAll} />
        ))}
        {tab === "quotes" && (
          <QuotesTab
            customers={customers}
            focusQuoteId={focusQuoteId}
            onOpenCalculator={() => switchTab("sales-calculator")}
            onStartContract={(quote) => {
              setContractPrefillQuote(quote);
              setTab("contracts");
              setSearchParams({ tab: "contracts", fromQuote: quote.id });
            }}
          />
        )}
        {tab === "quote-notices" && adminRole === "super_admin" && <QuoteNoticesTab />}
        {tab === "field-consultation" && (
          <FieldConsultation onOpenCalculator={() => switchTab("sales-calculator")} />
        )}
        {tab === "sales-calculator" && (
          <SalesCalculatorV2 customers={customers} onConverted={handleQuoteConverted} />
        )}
        {tab === "sales-prices" && adminRole === "super_admin" && <SalesPricesTab />}
        {tab === "contracts" && (isSalesAccount && authUser?.sales_level !== "sales_super_admin" ? <AccessDenied /> : (
          <ContractsTab
            customers={customers}
            focusContractId={focusContractId}
            prefillQuote={contractPrefillQuote}
            onPrefillConsumed={() => setContractPrefillQuote(null)}
          />
        ))}
        {tab === "contract-clauses" && adminRole === "super_admin" && <ContractClausesTab />}
        {tab === "delete-requests" && adminRole === "super_admin" && <DeleteRequestsTab />}
        {tab === "projects" && (isSalesAccount ? <AccessDenied /> : <ProjectsTab data={projects} customers={customers} onChange={fetchAll} />)}
        {tab === "customers" && (isSalesAccount ? <AccessDenied /> : <CustomersTab data={customers} onChange={fetchAll} />)}
        {tab === "users" && adminRole === "super_admin" && <AdminUsersTab />}
        {tab === "cms" && adminRole === "super_admin" && (
          <CmsTab category="cms" onSaved={refreshCms} />
        )}
        {tab === "media" && adminRole === "super_admin" && (
          <MediaTab onSaved={refreshCms} />
        )}
        {tab === "portfolio" && adminRole === "super_admin" && (
          <PortfolioCmsTab />
        )}
        {tab === "site" && adminRole === "super_admin" && (
          <CmsTab category="site" onSaved={refreshCms} />
        )}
        {tab === "seo" && adminRole === "super_admin" && (
          <SeoAeoGeoTab onSaved={refreshCms} />
        )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs uppercase tracking-[0.2em] text-white/40">{label}</span>
        <div
          className={`w-8 h-8 rounded-lg flex items-center justify-center ${
            accent ? "bg-[#0055FF]/20 text-[#0055FF]" : "bg-white/5 text-white/70"
          }`}
        >
          <Icon size={14} />
        </div>
      </div>
      <div className="font-display text-3xl md:text-4xl font-semibold text-white tracking-tight">
        {value ?? "—"}
      </div>
    </div>
  );
}

function Overview({ stats, inquiries = [], onOpenInquiries, onOpenQuotes, onOpenContracts }) {
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [recentContracts, setRecentContracts] = useState([]);
  useEffect(() => {
    api.get("/admin/quotes").then((r) => setRecentQuotes((r.data || []).slice(0, 5))).catch(() => {});
    api.get("/admin/contracts").then((r) => setRecentContracts((r.data || []).slice(0, 5))).catch(() => {});
  }, []);
  if (!stats) return <div className="text-white/50 text-sm">로드 중...</div>;
  const recent = inquiries.slice(0, 5);
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="전체 문의" value={stats.total_inquiries} icon={Inbox} />
        <StatCard label="신규 문의" value={stats.new_inquiries} icon={Inbox} accent />
        <StatCard label="상담 완료" value={stats.won_inquiries} icon={CheckCircle2} />
        <StatCard label="고객 수" value={stats.total_customers} icon={Users} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard label="전체 프로젝트" value={stats.total_projects} icon={Briefcase} />
        <StatCard label="진행 중" value={stats.active_projects} icon={TrendingUp} accent />
        <StatCard
          label="상담 완료율"
          value={
            stats.total_inquiries
              ? `${Math.round((stats.won_inquiries / stats.total_inquiries) * 100)}%`
              : "0%"
          }
          icon={TrendingUp}
        />
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
        <div className="p-6 border-b border-white/10 flex items-center justify-between gap-4">
          <div>
            <h3 className="font-display text-lg font-medium">최근 문의</h3>
            <p className="text-xs text-white/40 mt-1">
              홈페이지 문의 접수 시 메일과 함께 여기에도 자동으로 표시됩니다.
            </p>
          </div>
          <button
            type="button"
            onClick={onOpenInquiries}
            className="text-xs border border-white/10 hover:border-white/30 px-3 py-1.5 rounded-full whitespace-nowrap"
          >
            문의 관리 전체 보기
          </button>
        </div>
        {recent.length === 0 ? (
          <div className="p-10 text-center text-white/40 text-sm">아직 접수된 문의가 없습니다.</div>
        ) : (
          <div className="divide-y divide-white/5">
            {recent.map((q) => (
              <button
                key={q.id}
                type="button"
                onClick={onOpenInquiries}
                className="w-full text-left px-6 py-4 hover:bg-white/[0.02] transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-sm text-white font-medium">
                      {q.name}
                      {q.company ? <span className="text-white/40 font-normal"> · {q.company}</span> : null}
                    </div>
                    <div className="text-xs text-white/50 mt-1 line-clamp-2">{q.message}</div>
                    <div className="text-[11px] text-white/30 mt-2">
                      {q.email}{q.phone ? ` · ${q.phone}` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="inline-flex rounded-full border border-blue-500/20 bg-blue-500/10 px-2 py-0.5 text-[11px] text-blue-200">
                      {INQUIRY_STATUS_LABEL[q.status] || "문의"}
                    </span>
                    <div className="text-[10px] text-white/30 mt-2">
                      {q.created_at ? new Date(q.created_at).toLocaleString("ko-KR") : ""}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium">최근 견적</h3>
            <button type="button" onClick={onOpenQuotes} className="text-xs border border-white/10 px-3 py-1.5 rounded-full">전체 보기</button>
          </div>
          {recentQuotes.length === 0 ? (
            <div className="p-6 text-center text-white/40 text-sm">최근 견적이 없습니다.</div>
          ) : recentQuotes.map((q) => (
            <button key={q.id} type="button" onClick={onOpenQuotes} className="w-full text-left px-4 py-3 border-t border-white/5 hover:bg-white/[0.02]">
              <div className="text-sm">{q.title}</div>
              <div className="text-[11px] text-white/40 mt-0.5">{q.customer_email || "-"} · {(q.total_amount ?? q.total ?? 0).toLocaleString()}원</div>
            </button>
          ))}
        </div>
        <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between gap-3">
            <h3 className="text-sm font-medium">최근 계약</h3>
            <button type="button" onClick={onOpenContracts} className="text-xs border border-white/10 px-3 py-1.5 rounded-full">전체 보기</button>
          </div>
          {recentContracts.length === 0 ? (
            <div className="p-6 text-center text-white/40 text-sm">최근 계약이 없습니다.</div>
          ) : recentContracts.map((c) => (
            <button key={c.id} type="button" onClick={onOpenContracts} className="w-full text-left px-4 py-3 border-t border-white/5 hover:bg-white/[0.02]">
              <div className="text-sm">{c.title}</div>
              <div className="text-[11px] text-white/40 mt-0.5">{c.customer_email || "-"} · {(c.contract_amount || 0).toLocaleString()}원</div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-display text-lg font-medium">월별 문의 추이</h3>
          <span className="text-xs text-white/40">최근 활동</span>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={
                stats.monthly_inquiries.length > 0
                  ? stats.monthly_inquiries
                  : [{ month: "—", count: 0 }]
              }
            >
              <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis
                dataKey="month"
                stroke="rgba(255,255,255,0.4)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                stroke="rgba(255,255,255,0.4)"
                fontSize={11}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  background: "#0a0a0a",
                  border: "1px solid rgba(255,255,255,0.1)",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
                cursor={{ fill: "rgba(255,255,255,0.04)" }}
              />
              <Bar dataKey="count" fill="#0055FF" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function InquiriesTab({ data, onChange }) {
  const [expandedId, setExpandedId] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const updateStatus = async (id, nextStatus) => {
    try {
      await api.patch(`/admin/inquiries/${id}`, { status: nextStatus });
      toast.success("상태가 업데이트되었습니다.");
      onChange();
    } catch {
      toast.error("업데이트 실패");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("이 문의를 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/admin/inquiries/${id}`);
      toast.success("삭제 완료");
      onChange();
    } catch {
      toast.error("삭제 실패");
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data || []).filter((row) => {
      if (status !== "all" && row.status !== status) return false;
      if (!needle) return true;
      const hay = [row.name, row.email, row.phone, row.company, row.message, row.service_type].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [data, q, status]);
  const paged = paginateItems(filtered, page, pageSize);

  return (
    <div data-testid="admin-inquiries">
      <AdminPageHeader
        title={`문의 관리`}
        description="고객이 홈페이지에서 문의를 보내면 이메일과 함께 이 목록에 자동 저장됩니다."
      />
      <div className="admin-toolbar">
        <input className="admin-toolbar__search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="이름 / 연락처 / 상태 검색" />
        <select className="admin-filter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="all">전체 상태</option>
          {Object.entries(INQUIRY_STATUS_LABEL).filter(([k]) => ["received", "responding", "answered"].includes(k)).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <span className="admin-count">{filtered.length}건</span>
      </div>
      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
      {filtered.length === 0 ? (
        <AdminEmptyState message="아직 문의가 없습니다." />
      ) : (
        <div className="divide-y divide-white/5">
          {paged.items.map((q) => {
            const open = expandedId === q.id;
            return (
              <div key={q.id} className="p-5 md:p-6">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <button
                    type="button"
                    onClick={() => setExpandedId(open ? null : q.id)}
                    className="flex-1 text-left"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-white font-medium">{q.name}</span>
                      {q.company && <span className="text-white/40 text-sm">· {q.company}</span>}
                      <span className="text-[11px] text-white/30">
                        {q.created_at ? new Date(q.created_at).toLocaleString("ko-KR") : ""}
                      </span>
                    </div>
                    <div className="text-xs text-white/50 mt-1">
                      {q.email}{q.phone ? ` · ${q.phone}` : ""}
                      {q.service_type ? ` · ${q.service_type}` : ""}
                    </div>
                    <div className="text-xs text-[#0055FF] mt-2">
                      {open ? "양식 접기" : "문의 양식 전체 보기"}
                    </div>
                  </button>
                  <div className="flex items-center gap-2 shrink-0">
                    <select
                      value={INQUIRY_STATUS_LABEL[q.status] ? q.status : "received"}
                      onChange={(e) => updateStatus(q.id, e.target.value)}
                      data-testid={`inquiry-status-${q.id}`}
                      className="bg-white/[0.03] border border-white/10 rounded-md px-2 py-1.5 text-xs text-white"
                    >
                      {Object.entries(INQUIRY_STATUS_LABEL)
                        .filter(([k]) => ["received", "responding", "answered"].includes(k))
                        .map(([k, v]) => (
                          <option key={k} value={k}>{v}</option>
                        ))}
                    </select>
                    <button
                      onClick={() => remove(q.id)}
                      data-testid={`inquiry-delete-${q.id}`}
                      className="text-white/40 hover:text-red-400 p-1"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                {open && <InquiryFormDetail inquiry={q} />}
              </div>
            );
          })}
        </div>
      )}
      </div>
      <AdminPagination
        page={paged.page}
        pageSize={pageSize}
        total={filtered.length}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
      />
    </div>
  );
}

function ProjectsTab({ data, customers, onChange }) {
  const [form, setForm] = useState({
    title: "",
    customer_id: "",
    client: "",
    description: "",
    status: "planning",
    progress: 0,
  });
  const [showForm, setShowForm] = useState(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const onCustomerPick = (customerId) => {
    const customer = customers.find((c) => c.id === customerId);
    setForm({
      ...form,
      customer_id: customerId,
      client: customer ? customer.name : "",
    });
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("프로젝트명은 필수입니다.");
    if (!form.customer_id && !form.client) return toast.error("고객을 선택하거나 클라이언트명을 입력해 주세요.");
    try {
      await api.post("/admin/projects", { ...form, progress: Number(form.progress) });
      toast.success("프로젝트가 추가되었습니다.");
      setForm({ title: "", customer_id: "", client: "", description: "", status: "planning", progress: 0 });
      setShowForm(false);
      onChange();
    } catch {
      toast.error("추가 실패");
    }
  };
  const remove = async (id) => {
    if (!window.confirm("삭제하시겠습니까?")) return;
    await api.delete(`/admin/projects/${id}`);
    onChange();
  };
  const updateStatus = async (p, status) => {
    await api.patch(`/admin/projects/${p.id}`, { ...p, status });
    onChange();
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data || []).filter((p) => {
      if (statusFilter !== "all" && p.status !== statusFilter) return false;
      if (!needle) return true;
      return [p.title, p.client, p.description].filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
  }, [data, q, statusFilter]);
  const paged = paginateItems(filtered, page, 20);

  return (
    <div className="space-y-4" data-testid="admin-projects">
      <AdminPageHeader
        title="프로젝트"
        description="진행 중인 프로젝트와 고객 연동을 관리합니다."
        actions={(
          <button type="button" className="bg-white text-black rounded-lg text-sm font-medium px-4 py-2" onClick={() => setShowForm((v) => !v)}>
            {showForm ? "닫기" : "+ 항목 추가"}
          </button>
        )}
      />
      <div className="admin-toolbar">
        <input className="admin-toolbar__search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="프로젝트명 / 고객 / 상태 검색" />
        <select className="admin-filter" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">전체 상태</option>
          {Object.entries(PROJECT_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span className="admin-count">{filtered.length}건</span>
      </div>
      {showForm && (
      <form
        onSubmit={submit}
        className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 grid grid-cols-1 md:grid-cols-6 gap-3"
      >
        <input
          placeholder="프로젝트명"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          data-testid="project-title"
          className="md:col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
        />
        <select
          value={form.customer_id}
          onChange={(e) => onCustomerPick(e.target.value)}
          className="md:col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">고객 선택 (CRM 연동)</option>
          {customers.map((c) => (
            <option key={c.id} value={c.id}>{c.name} · {c.email}</option>
          ))}
        </select>
        <input
          placeholder="클라이언트명"
          value={form.client}
          onChange={(e) => setForm({ ...form, client: e.target.value })}
          data-testid="project-client"
          className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
        />
        <select
          value={form.status}
          onChange={(e) => setForm({ ...form, status: e.target.value })}
          className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
        >
          {Object.entries(PROJECT_STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <button
          type="submit"
          data-testid="project-add"
          className="bg-white text-black rounded-lg text-sm font-medium px-4 py-2 flex items-center justify-center gap-1.5"
        >
          <Plus size={14} /> 추가
        </button>
      </form>
      )}

      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8"><AdminEmptyState message="프로젝트가 없습니다." action={<button type="button" className="admin-row__btn" onClick={() => setShowForm(true)}>항목 추가</button>} /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-white/40 border-b border-white/10">
              <tr>
                <th className="text-left py-3 px-5">프로젝트</th>
                <th className="text-left py-3 px-5">클라이언트</th>
                <th className="text-left py-3 px-5">상태</th>
                <th className="text-left py-3 px-5">날짜</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paged.items.map((p) => (
                <tr key={p.id} className="border-b border-white/5">
                  <td className="py-3 px-5 text-white">{p.title}</td>
                  <td className="py-3 px-5 text-white/70">{p.client}</td>
                  <td className="py-3 px-5">
                    <select
                      value={p.status}
                      onChange={(e) => updateStatus(p, e.target.value)}
                      className="bg-white/[0.03] border border-white/10 rounded-md px-2 py-1 text-xs"
                    >
                      {Object.entries(PROJECT_STATUS_LABEL).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </td>
                  <td className="py-3 px-5 text-xs text-white/40">
                    {new Date(p.created_at).toLocaleDateString("ko-KR")}
                  </td>
                  <td className="py-3 px-5">
                    <button onClick={() => remove(p.id)} className="text-white/40 hover:text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <AdminPagination page={paged.page} pageSize={20} total={filtered.length} onPageChange={setPage} />
    </div>
  );
}

function StatusBadge({ label, tone = "default" }) {
  const tones = {
    default: "bg-white/5 text-white/70 border-white/10",
    inquiry: "bg-blue-500/10 text-blue-200 border-blue-500/20",
    quote: "bg-violet-500/10 text-violet-200 border-violet-500/20",
    project: "bg-emerald-500/10 text-emerald-200 border-emerald-500/20",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${tones[tone] || tones.default}`}>
      {label || "—"}
    </span>
  );
}

function CustomersTab({ data, onChange }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    notes: "",
  });
  const [showForm, setShowForm] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("이름·이메일은 필수입니다.");
    try {
      await api.post("/admin/customers", form);
      toast.success("고객이 추가되었습니다.");
      setForm({ name: "", email: "", phone: "", company: "", notes: "" });
      setShowForm(false);
      onChange();
    } catch {
      toast.error("추가 실패");
    }
  };
  const remove = async (id) => {
    if (
      !window.confirm(
        "고객 CRM과 연결된 로그인 계정도 함께 삭제됩니다.\n같은 이메일로 다시 회원가입할 수 있습니다.\n삭제하시겠습니까?",
      )
    ) {
      return;
    }
    await api.delete(`/admin/customers/${id}`);
    onChange();
  };
  const updateNotes = async (c, notes) => {
    await api.patch(`/admin/customers/${c.id}`, {
      name: c.name,
      email: c.email,
      phone: c.phone || "",
      company: c.company || "",
      notes,
    });
    onChange();
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return data || [];
    return (data || []).filter((c) => [c.name, c.email, c.phone, c.company, c.notes].filter(Boolean).join(" ").toLowerCase().includes(needle));
  }, [data, q]);
  const paged = paginateItems(filtered, page, 20);

  return (
    <div className="space-y-4" data-testid="admin-customers">
      <AdminPageHeader
        title="고객 CRM"
        description="문의 · 견적 · 프로젝트 상태는 각 관리 메뉴에서 변경되면 CRM에 자동 반영됩니다."
        actions={(
          <button type="button" className="bg-white text-black rounded-lg text-sm font-medium px-4 py-2" onClick={() => setShowForm((v) => !v)}>
            {showForm ? "닫기" : "+ 고객 추가"}
          </button>
        )}
      />
      <div className="admin-toolbar">
        <input className="admin-toolbar__search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="이름 / 이메일 / 회사 검색" />
        <span className="admin-count">{filtered.length}명</span>
      </div>
      {showForm && (
      <form
        onSubmit={submit}
        className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 grid grid-cols-1 md:grid-cols-6 gap-3"
      >
        <input
          placeholder="이름"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          data-testid="customer-name"
          className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
        />
        <input
          placeholder="이메일"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="customer-email"
          className="md:col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
        />
        <input
          placeholder="회사"
          value={form.company}
          onChange={(e) => setForm({ ...form, company: e.target.value })}
          className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
        />
        <input
          placeholder="메모"
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
        />
        <button
          type="submit"
          data-testid="customer-add"
          className="bg-white text-black rounded-lg text-sm font-medium px-4 py-2 flex items-center justify-center gap-1.5"
        >
          <Plus size={14} /> 추가
        </button>
      </form>
      )}

      <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <div className="p-8"><AdminEmptyState message="아직 고객이 없습니다." /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-white/40 border-b border-white/10">
              <tr>
                <th className="text-left py-3 px-5">이름</th>
                <th className="text-left py-3 px-5">연락처</th>
                <th className="text-left py-3 px-5">회사</th>
                <th className="text-left py-3 px-5">문의</th>
                <th className="text-left py-3 px-5">견적</th>
                <th className="text-left py-3 px-5">프로젝트</th>
                <th className="text-left py-3 px-5">메모</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {paged.items.map((c) => (
                <tr key={c.id} className="border-b border-white/5">
                  <td className="py-3 px-5 text-white">{c.name}</td>
                  <td className="py-3 px-5 text-white/70">
                    <div>{c.email}</div>
                    {c.phone && <div className="text-xs text-white/40">{c.phone}</div>}
                  </td>
                  <td className="py-3 px-5 text-white/70">{c.company || "—"}</td>
                  <td className="py-3 px-5">
                    <StatusBadge
                      tone="inquiry"
                      label={INQUIRY_STATUS_LABEL[c.inquiry_status] || "—"}
                    />
                  </td>
                  <td className="py-3 px-5">
                    <StatusBadge
                      tone="quote"
                      label={QUOTE_STATUS_LABEL[c.quote_status] || "—"}
                    />
                  </td>
                  <td className="py-3 px-5">
                    <StatusBadge
                      tone="project"
                      label={PROJECT_STATUS_LABEL[c.project_status] || "—"}
                    />
                  </td>
                  <td className="py-3 px-5">
                    <input
                      defaultValue={c.notes || ""}
                      onBlur={(e) => {
                        if (e.target.value !== (c.notes || "")) updateNotes(c, e.target.value);
                      }}
                      placeholder="메모"
                      className="bg-white/[0.03] border border-white/10 rounded-md px-2 py-1 text-xs w-full min-w-[120px]"
                    />
                  </td>
                  <td className="py-3 px-5">
                    <button onClick={() => remove(c.id)} className="text-white/40 hover:text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <AdminPagination page={paged.page} pageSize={20} total={filtered.length} onPageChange={setPage} />
    </div>
  );
}

const EMPTY_QUOTE_ITEM = { name: "", scope: "", duration: "", amount: 0, note: "", quantity: 1 };
const EMPTY_QUOTE_FORM = {
  title: "",
  customer_id: "",
  customer_email: "",
  valid_until: "",
  notes: "",
  items: [{ ...EMPTY_QUOTE_ITEM }],
  notice_snapshot: [],
};

const QUOTE_KIND_FILTERS = [
  { id: "all", label: "전체" },
  { id: "provisional", label: "임시" },
  { id: "review", label: "기술검토" },
  { id: "final", label: "최종" },
  { id: "approved", label: "승인완료" },
];

function quoteMatchesKindFilter(row, kindFilter) {
  if (!kindFilter || kindFilter === "all") return true;
  const life = row.lifecycle || {};
  if (kindFilter === "provisional") return life.document_kind === "provisional" || row.pricing_kind === "provisional";
  if (kindFilter === "review") return ["PENDING", "IN_REVIEW"].includes(row.technical_review_status);
  if (kindFilter === "final") return life.document_kind === "final" || row.pricing_kind === "final";
  if (kindFilter === "approved") return row.status === "approved";
  return true;
}

function QuotesTab({ customers, focusQuoteId, onStartContract, onOpenCalculator }) {
  const { user } = useAuth();
  const isOwner = user?.role === "super_admin";
  const [quotes, setQuotes] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [chatRoom, setChatRoom] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_QUOTE_FORM, items: [{ ...EMPTY_QUOTE_ITEM }] });
  const [noticesLocked, setNoticesLocked] = useState(false);
  const [noticeSync, setNoticeSync] = useState(null);
  const [fallbackNotices, setFallbackNotices] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [quoteToDelete, setQuoteToDelete] = useState(null);
  const [hardDeleting, setHardDeleting] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const sendingLock = useRef(false);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [expandedId, setExpandedId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchOpen, setBatchOpen] = useState(false);

  const resetForm = () => {
    setForm({ ...EMPTY_QUOTE_FORM, items: [{ ...EMPTY_QUOTE_ITEM }] });
    setEditingId(null);
    setShowForm(false);
    setNoticesLocked(false);
  };

  const load = async () => {
    try { const r = await api.get("/admin/quotes"); setQuotes(r.data); } catch { toast.error("로드 실패"); }
  };
  useEffect(() => { load(); }, []);

  const visibleQuotes = useMemo(() => {
    const needle = q.trim().toLowerCase();
    let list = quotes;
    if (statusFilter !== "all") list = list.filter((row) => row.status === statusFilter);
    if (kindFilter !== "all") list = list.filter((row) => quoteMatchesKindFilter(row, kindFilter));
    if (needle) {
      list = list.filter((row) => [row.title, row.customer_email, row.customer_name, row.status].filter(Boolean).join(" ").toLowerCase().includes(needle));
    }
    if (!focusQuoteId) return list;
    const hit = list.filter((row) => row.id === focusQuoteId);
    const rest = list.filter((row) => row.id !== focusQuoteId);
    return [...hit, ...rest];
  }, [quotes, focusQuoteId, q, statusFilter, kindFilter]);
  const pagedQuotes = paginateItems(visibleQuotes, page, 20);

  useEffect(() => {
    if (!focusQuoteId) return;
    setExpandedId(focusQuoteId);
    const reviewEl = document.querySelector(`[data-testid="quote-tech-review-${focusQuoteId}"]`);
    const el = reviewEl || document.querySelector(`[data-testid="admin-quote-${focusQuoteId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusQuoteId, visibleQuotes]);

  const openCreate = async () => {
    setEditingId(null);
    setNoticesLocked(false);
    let snapshot = [];
    try {
      const r = await api.get("/admin/quote-notices");
      snapshot = normalizeNoticeRows((r.data.lines || []).filter((row) => row.active !== false));
      setFallbackNotices(normalizeNoticeRows(r.data.fallback_lines || []));
    } catch {
      toast.error("기본 안내문을 불러오지 못했습니다.");
    }
    setForm({ ...EMPTY_QUOTE_FORM, items: [{ ...EMPTY_QUOTE_ITEM }], notice_snapshot: snapshot });
    setShowForm(true);
  };

  const openEdit = async (q) => {
    setEditingId(q.id);
    const hasSnap = Array.isArray(q.notice_snapshot);
    setNoticesLocked(!hasSnap);
    try {
      const r = await api.get("/admin/quote-notices");
      setFallbackNotices(normalizeNoticeRows(r.data.fallback_lines || []));
    } catch { /* keep previous fallback */ }
    try {
      const s = await api.get(`/admin/quotes/${q.id}/notice-sync`);
      setNoticeSync(s.data);
    } catch { setNoticeSync(null); }
    setForm({
      title: q.title || "",
      customer_id: q.customer_id || "",
      customer_email: q.customer_email || "",
      valid_until: q.valid_until || "",
      notes: q.notes || "",
      notice_snapshot: hasSnap ? normalizeNoticeRows(q.notice_snapshot) : [],
      items: (q.items?.length ? q.items : [{ ...EMPTY_QUOTE_ITEM }]).map((it) => ({
        name: it.name || "",
        scope: it.scope || "",
        duration: it.duration || "",
        amount: it.unit_price || it.amount || 0,
        note: it.note || "",
        quantity: it.quantity || 1,
      })),
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("제목 필수");
    const items = form.items
      .filter((i) => i.name?.trim() || i.scope?.trim() || Number(i.amount))
      .map((i) => ({
        ...i,
        amount: Number(i.amount) || 0,
        quantity: Math.max(Number(i.quantity) || 1, 1),
      }));
    if (items.length === 0) return toast.error("견적 항목을 1개 이상 입력해 주세요.");
    try {
      const payload = { ...form, items };
      delete payload.noticesLocked;
      if (form.customer_id) {
        const c = customers.find((c) => c.id === form.customer_id);
        if (c) payload.customer_email = c.email;
      }
      if (editingId && noticesLocked) {
        delete payload.notice_snapshot;
      } else {
        payload.notice_snapshot = normalizeNoticeRows(form.notice_snapshot || []);
      }
      if (editingId) {
        await api.patch(`/admin/quotes/${editingId}`, payload);
        toast.success("견적이 수정되었습니다.");
      } else {
        await api.post("/admin/quotes", payload);
        toast.success("견적 생성됨");
      }
      resetForm();
      load();
    } catch { toast.error("실패"); }
  };

  const sendQuote = async (qid, customerEmail, sendLabel) => {
    if (sendingLock.current) return;
    sendingLock.current = true;
    const label = sendLabel || "견적";
    if (!window.confirm(`고객(${customerEmail || "이메일 확인 필요"})에게 ${label}을 발송하시겠습니까?`)) {
      sendingLock.current = false;
      return;
    }
    setSendingId(qid);
    const loading = toast.loading("견적 메일 발송 중...");
    try {
      const r = await api.post(`/admin/quotes/${qid}/send`);
      const sentTo = r.data?.sent_to;
      const adminCopy = r.data?.admin_copy;
      toast.success(
        sentTo
          ? `발송 완료: ${sentTo}${adminCopy ? ` (관리자 사본: ${adminCopy})` : ""}`
          : "발송되었습니다.",
        { id: loading, duration: 6000 }
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "견적 발송에 실패했습니다.", { id: loading });
    } finally {
      sendingLock.current = false;
      setSendingId(null);
    }
  };

  const handleHardDeleteQuote = async () => {
    if (!quoteToDelete || hardDeleting) return;
    setHardDeleting(true);
    try {
      await api.delete(`/admin/quotes/${quoteToDelete.id}/hard`);
      toast.success("견적이 삭제되었습니다.");
      setQuoteToDelete(null);
      load();
      refreshAdminNotifications();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "삭제 실패");
    } finally {
      setHardDeleting(false);
    }
  };

  const copyQuote = async (qid) => { await api.post(`/admin/quotes/${qid}/copy`); load(); };
  const downloadPdf = async (qid) => {
    const loading = toast.loading("PDF 준비 중입니다...");
    try {
      const r = await api.get(`/admin/quotes/${qid}/pdf`, { responseType: "blob", timeout: 120000 });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filenameFromDisposition(r.headers?.["content-disposition"], `quote-${qid.slice(0, 8)}.pdf`);
      a.click();
      URL.revokeObjectURL(url);
      toast.success("다운로드합니다.", { id: loading });
    } catch (e) {
      let msg = "PDF 다운로드 실패";
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const j = JSON.parse(await data.text());
          if (j.detail) msg = j.detail;
        } catch { /* ignore */ }
      } else if (e?.response?.data?.detail) {
        msg = e.response.data.detail;
      }
      toast.error(msg, { id: loading });
    }
  };
  const updateItem = (i, k, v) => {
    const items = [...form.items]; items[i] = { ...items[i], [k]: v }; setForm({ ...form, items });
  };
  const addItem = () => setForm({ ...form, items: [...form.items, { ...EMPTY_QUOTE_ITEM }] });
  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== idx) });
  };
  const addTemplateItem = (template) => {
    const exists = form.items.some((it) => it.name === template.name);
    if (exists) {
      toast.message("이미 추가된 항목입니다.");
      return;
    }
    setForm({ ...form, items: [...form.items, quoteItemFromTemplate(template)] });
  };
  const applyAllTemplates = () => {
    const existingNames = new Set(form.items.map((it) => it.name).filter(Boolean));
    const toAdd = QUOTE_ITEM_TEMPLATES.filter((t) => !existingNames.has(t.name)).map(quoteItemFromTemplate);
    const kept = form.items.filter((it) => it.name?.trim() || it.scope?.trim() || Number(it.amount));
    setForm({
      ...form,
      items: kept.length || toAdd.length ? [...kept, ...toAdd] : [{ ...EMPTY_QUOTE_ITEM }],
    });
    toast.success(`${toAdd.length}개 템플릿 항목을 추가했습니다.`);
  };

  const QSTATUS = QUOTE_STATUS_LABEL;

  return (
    <div className="space-y-4" data-testid="admin-quotes">
      <AdminPageHeader
        title={`견적 관리`}
        description={`발송 시 고객 이메일로 PDF가 전송되며, 관리자 메일(${process.env.REACT_APP_COMPANY_EMAIL || "dsitesolution@dsitesolution.com"})에도 사본이 함께 발송됩니다.`}
        actions={(
          <button onClick={() => (showForm && !editingId ? resetForm() : openCreate())} data-testid="quote-toggle-form" className="bg-white text-black px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 shrink-0"><Plus size={14}/>새 견적</button>
        )}
      />
      <div className="admin-toolbar">
        <input className="admin-toolbar__search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="고객명 / 제목 / 이메일 / 상태 검색" />
        <select className="admin-filter" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">전체 상태</option>
          {Object.entries(QUOTE_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div className="admin-chip-row quote-kind-filters" data-testid="quote-kind-filters">
          {QUOTE_KIND_FILTERS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`admin-chip${kindFilter === item.id ? " is-on" : ""}`}
              onClick={() => { setKindFilter(item.id); setPage(1); }}
            >
              {item.label}
            </button>
          ))}
        </div>
        <span className="admin-count">{visibleQuotes.length}건</span>
      </div>
      <SalesBulkBar
        visibleIds={pagedQuotes.items.map((row) => row.id)}
        selectedIds={selectedIds}
        onToggleAll={(on) => setSelectedIds(on ? pagedQuotes.items.map((row) => row.id) : [])}
        onDelete={() => setBatchOpen(true)}
        deleteLabel={isOwner ? "선택항목 삭제" : "선택항목 삭제 요청"}
        testId="quote-bulk-bar"
      />
      {showForm && (
        <form onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-sm font-medium">{editingId ? "견적 수정" : "새 견적 작성"}</h4>
            <button type="button" onClick={resetForm} className="text-white/50 hover:text-white"><X size={16}/></button>
          </div>
          <input placeholder="견적 제목 (프로젝트명)" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} data-testid="quote-title" className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>
          <select value={form.customer_id} onChange={(e)=>setForm({...form,customer_id:e.target.value})} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm">
            <option value="">고객 선택 (또는 이메일 직접 입력)</option>
            {customers.map(c=><option key={c.id} value={c.id}>{c.name} - {c.email}</option>)}
          </select>
          {!form.customer_id && <input placeholder="고객 이메일" value={form.customer_email} onChange={(e)=>setForm({...form,customer_email:e.target.value})} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input placeholder="유효기간 (예: 2026-07-31)" value={form.valid_until} onChange={(e)=>setForm({...form,valid_until:e.target.value})} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>
            <input placeholder="비고 (PDF 하단 표시)" value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>
          </div>
          <div className="space-y-2 border-t border-white/10 pt-3">
            <div className="text-xs text-white/50 font-medium">견적서 안내문 (이 견적만)</div>
            {noticesLocked ? (
              <div className="space-y-2">
                <p className="text-[11px] text-white/40">기존 견적입니다. 저장해도 안내문은 기존 PDF 문구를 유지합니다.</p>
                <div className="text-[11px] text-white/35 space-y-1 max-h-40 overflow-auto">
                  {(fallbackNotices.length ? fallbackNotices : []).map((row) => (
                    <div key={row.id}>{row.text}</div>
                  ))}
                </div>
                <button
                  type="button"
                  className="text-xs border border-white/15 rounded px-3 py-1.5"
                  onClick={() => {
                    setForm({ ...form, notice_snapshot: fallbackNotices.length ? fallbackNotices : [] });
                    setNoticesLocked(false);
                  }}
                >
                  이 견적 안내문 수정
                </button>
              </div>
            ) : (
              <>
                {quotes.find((row) => row.id === editingId)?.pricing_kind === "final" ? (
                  <p className="text-[11px] text-white/50 leading-relaxed">최종 견적 안내문은 발송·확정된 요구사항 기준으로 유지됩니다.</p>
                ) : (
                  <p className="text-[11px] text-amber-200 leading-relaxed">{PROVISIONAL_QUOTE_NOTICE}</p>
                )}
                {noticeSync?.master_updated && noticeSync?.syncable && (
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-amber-300">
                    <span>공통 조항이 변경되었습니다.</span>
                    <button
                      type="button"
                      className="text-xs border border-amber-300/40 rounded px-3 py-1.5"
                      onClick={async () => {
                        try {
                          const r = await api.post(`/admin/quotes/${editingId}/sync-notices`, { mode: "all" });
                          setForm((f) => ({ ...f, notice_snapshot: normalizeNoticeRows(r.data.notice_snapshot || []) }));
                          setNoticeSync((s) => ({ ...(s || {}), master_updated: false }));
                          toast.success("최신 안내문을 반영했습니다.");
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "반영 실패");
                        }
                      }}
                    >
                      최신 조항 반영
                    </button>
                  </div>
                )}
                {noticeSync?.frozen && (
                  <p className="text-[11px] text-white/40">발송·확정된 견적은 공통 안내문이 자동 변경되지 않습니다.</p>
                )}
                <QuoteNoticeEditor
                  lines={form.notice_snapshot || []}
                  onChange={(next) => setForm({ ...form, notice_snapshot: next })}
                />
              </>
            )}
          </div>
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="text-xs text-white/50 font-medium">세부 견적 항목</div>
              <button
                type="button"
                onClick={applyAllTemplates}
                data-testid="quote-apply-all-templates"
                className="text-[11px] border border-white/15 rounded-lg px-2.5 py-1 text-white/70 hover:border-white/30 hover:text-white"
              >
                전체 템플릿 적용
              </button>
            </div>
            <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 space-y-2">
              <div className="text-[11px] text-white/45">목록 템플릿 — 클릭하면 항목이 추가됩니다</div>
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto pr-1">
                {QUOTE_ITEM_TEMPLATES.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => addTemplateItem(template)}
                    data-testid={`quote-template-${template.id}`}
                    className="text-left text-[11px] leading-snug rounded-lg border border-white/10 bg-white/[0.03] px-2 py-1.5 text-white/75 hover:border-violet-400/40 hover:bg-violet-500/10 hover:text-white"
                    title={template.scope}
                  >
                    {template.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="hidden md:grid grid-cols-12 gap-2 text-[10px] uppercase tracking-wider text-white/35 px-1">
              <span className="col-span-2">항목명 (한글)</span>
              <span className="col-span-3">세부 견적 사항 (영문)</span>
              <span className="col-span-2">기간</span>
              <span className="col-span-2">금액(원)</span>
              <span className="col-span-1">수량</span>
              <span className="col-span-2">비고</span>
            </div>
            {form.items.map((it,i)=>(
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <input placeholder="항목명 (한글)" value={it.name} onChange={(e)=>updateItem(i,"name",e.target.value)} className="col-span-6 md:col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-2 py-2 text-sm"/>
                <input placeholder="세부 견적 사항 (영문)" value={it.scope} onChange={(e)=>updateItem(i,"scope",e.target.value)} className="col-span-6 md:col-span-3 bg-white/[0.03] border border-white/10 rounded-lg px-2 py-2 text-sm"/>
                <input placeholder="기간" value={it.duration} onChange={(e)=>updateItem(i,"duration",e.target.value)} className="col-span-4 md:col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-2 py-2 text-sm"/>
                <input type="number" placeholder="금액" value={it.amount} onChange={(e)=>updateItem(i,"amount",e.target.value)} className="col-span-4 md:col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-2 py-2 text-sm"/>
                <input type="number" min="1" placeholder="수량" value={it.quantity ?? 1} onChange={(e)=>updateItem(i,"quantity",e.target.value)} className="col-span-2 md:col-span-1 bg-white/[0.03] border border-white/10 rounded-lg px-2 py-2 text-sm"/>
                <div className="col-span-6 md:col-span-2 flex gap-1">
                  <input placeholder="비고" value={it.note} onChange={(e)=>updateItem(i,"note",e.target.value)} className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-2 py-2 text-sm"/>
                  <button type="button" onClick={()=>removeItem(i)} disabled={form.items.length<=1} className="px-2 text-white/40 hover:text-red-400 disabled:opacity-20" title="항목 삭제"><X size={14}/></button>
                </div>
              </div>
            ))}
            <button type="button" onClick={addItem} className="text-xs border border-dashed border-white/20 rounded-lg px-3 py-2 text-white/70 hover:border-white/40 hover:text-white w-full sm:w-auto">
              + 견적 항목 추가
            </button>
          </div>
          <div className="flex gap-2">
            <button type="submit" data-testid="quote-submit" className="bg-white text-black px-4 py-2 rounded-lg text-sm">{editingId ? "수정 저장" : "생성"}</button>
            <button type="button" onClick={resetForm} className="border border-white/10 px-4 py-2 rounded-lg text-sm text-white/60">취소</button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {visibleQuotes.length === 0 && (
          <AdminEmptyState
            message="아직 생성된 견적이 없습니다."
            action={<button type="button" className="admin-row__btn" onClick={() => onOpenCalculator?.()}>견적 계산 시작</button>}
          />
        )}
        {pagedQuotes.items.map(q => (
          <div
            key={q.id}
            data-testid={`admin-quote-${q.id}`}
            className={`bg-[#0a0a0a] border rounded-xl px-4 py-3 ${focusQuoteId === q.id ? "admin-quote-card--focus" : "border-white/10"}`}
          >
            <div className="flex justify-between items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 shrink-0"
                checked={selectedIds.includes(q.id)}
                onChange={(e) => setSelectedIds((prev) => toggleId(prev, q.id, e.target.checked))}
                data-testid={`quote-check-${q.id}`}
              />
              <button type="button" className="text-left min-w-0 flex-1" onClick={() => setExpandedId(expandedId === q.id ? null : q.id)}>
                <div className="font-medium">{q.title}</div>
                <div className="text-sm text-white/80 mt-1">
                  공급가 {formatWon(q.supply_amount ?? q.total ?? 0)}
                  <span className="text-white/40"> · </span>
                  총액 {formatWon(q.total_amount ?? Math.round((q.supply_amount ?? q.total ?? 0) * 1.1))}
                </div>
                <QuoteLifecycleBadges lifecycle={q.lifecycle} testId={`quote-badges-${q.id}`} />
                {q.lifecycle?.has_active_contract ? (
                  <div className="text-xs text-amber-200 mt-1" data-testid={`quote-linked-contract-${q.id}`}>
                    연결 계약 있음{q.lifecycle.linked_contract_title ? ` · ${q.lifecycle.linked_contract_title}` : ""}
                  </div>
                ) : null}
                <div className="text-xs text-white/40 mt-1">
                  {q.customer_name || q.customer_email || "-"}
                  {" · "}{QSTATUS[q.status]}
                  {" · "}{q.created_at ? new Date(q.created_at).toLocaleDateString("ko-KR") : ""}
                  {q.created_by_name ? ` · 작성 ${q.created_by_name}` : ""}
                  {q.lifecycle?.link_hint ? ` · ${q.lifecycle.link_hint}` : ""}
                </div>
                {q.customer_message && <div className="text-xs text-amber-300 mt-2">고객 메시지: {q.customer_message}</div>}
              </button>
              <div className="flex flex-wrap gap-2">
                <button onClick={()=>openEdit(q)} className="text-xs border border-white/10 px-3 py-1.5 rounded-full flex items-center gap-1"><Edit3 size={12}/>수정</button>
                <button onClick={()=>downloadPdf(q.id)} className="text-xs border border-white/10 px-3 py-1.5 rounded-full flex items-center gap-1"><Download size={12}/>PDF</button>
                <button
                  onClick={()=>sendQuote(q.id, q.customer_email, q.lifecycle?.send_label)}
                  disabled={sendingId === q.id || q.lifecycle?.send_allowed === false}
                  title={q.lifecycle?.send_block_reason || ""}
                  className="text-xs border border-white/10 px-3 py-1.5 rounded-full flex items-center gap-1 disabled:opacity-50"
                  data-testid={`quote-send-${q.id}`}
                >
                  <Send size={12}/>{sendingId === q.id ? "발송 중..." : (q.lifecycle?.send_label || "발송")}
                </button>
                <button onClick={()=>copyQuote(q.id)} className="text-xs border border-white/10 px-3 py-1.5 rounded-full flex items-center gap-1"><Copy size={12}/>복사</button>
                <button onClick={()=>setChatRoom(q.id)} className="text-xs border border-white/10 px-3 py-1.5 rounded-full">채팅</button>
                <button onClick={()=>setDeleteTarget({ type: "quote", id: q.id, title: q.title, customer: q.customer_name || q.customer_email })} className="text-xs text-amber-300" data-testid={`quote-delete-${q.id}`}>
                  {isOwner ? "보관" : "삭제 요청"}
                </button>
                {isOwner && (
                  <button
                    onClick={()=>setQuoteToDelete(q)}
                    className="text-xs text-red-400 flex items-center gap-1"
                    data-testid={`quote-hard-delete-${q.id}`}
                  >
                    <Trash2 size={12}/>삭제
                  </button>
                )}
              </div>
            </div>
            {expandedId === q.id && (
              <div className="mt-3 space-y-1 border-t border-white/5 pt-3">
                {q.lifecycle?.document_label ? (
                  <p className="text-xs text-white/80 font-medium" data-testid={`quote-kind-${q.id}`}>{q.lifecycle.document_label}</p>
                ) : null}
                {q.lifecycle?.detail_notice ? (
                  <p className="text-[11px] text-amber-200 leading-relaxed">{q.lifecycle.detail_notice}</p>
                ) : null}
                {q.lifecycle?.send_block_reason ? (
                  <p className="text-[11px] text-amber-200 leading-relaxed">{q.lifecycle.send_block_reason}</p>
                ) : null}
                <div className="text-[11px] text-white/50 space-y-0.5">
                  <div>고객 승인: {q.lifecycle?.approve_hint || QSTATUS[q.status]}</div>
                  {q.lifecycle?.contract_hint ? <div>{q.lifecycle.contract_hint}</div> : <div>계약 전환: 가능</div>}
                  {q.last_sent_at ? <div>최근 발송: {new Date(q.last_sent_at).toLocaleString("ko-KR")}{q.send_count ? ` · ${q.send_count}회` : ""}</div> : null}
                  {q.pricing_kind === "final" && q.finalized_by ? <div>최종화 {q.finalized_by} · {q.finalized_at ? new Date(q.finalized_at).toLocaleString("ko-KR") : ""}</div> : null}
                </div>
                {(q.original_provisional_total != null || q.final_supply_price != null || q.technical_review_adjustment != null) ? (
                  <div className="quote-price-history" data-testid={`quote-price-history-${q.id}`}>
                    {q.original_provisional_total != null ? <div><span>초기 임시 공급가</span><b>{formatWon(q.original_provisional_total)}</b></div> : null}
                    {q.technical_review_adjustment != null ? <div><span>기술검토 조정</span><b>{formatSignedWon(q.technical_review_adjustment)}</b></div> : null}
                    {q.final_supply_price != null ? <div><span>최종 공급가</span><b>{formatWon(q.final_supply_price)}</b></div> : null}
                  </div>
                ) : null}
                <div className="flex flex-wrap gap-2 pt-1">
                  {q.final_quote_id ? (
                    <button type="button" className="admin-row__btn" onClick={() => { setKindFilter("all"); setExpandedId(q.final_quote_id); }} data-testid={`quote-open-final-${q.id}`}>최종 견적 보기</button>
                  ) : null}
                  {q.source_provisional_quote_id ? (
                    <button type="button" className="admin-row__btn" onClick={() => { setKindFilter("all"); setExpandedId(q.source_provisional_quote_id); }} data-testid={`quote-open-provisional-${q.id}`}>원본 임시 견적 보기</button>
                  ) : null}
                  <button
                    type="button"
                    className="admin-row__btn"
                    disabled={q.lifecycle?.contract_allowed === false}
                    title={q.lifecycle?.contract_hint || ""}
                    onClick={() => onStartContract?.(q)}
                    data-testid={`quote-to-contract-${q.id}`}
                  >
                    계약으로 전환
                  </button>
                  {q.lifecycle?.contract_allowed === false && q.lifecycle?.contract_hint ? (
                    <p className="text-[11px] text-amber-200 w-full">{q.lifecycle.contract_hint}</p>
                  ) : null}
                </div>
                {q.custom_consult_required && q.pricing_kind !== "final" && <p className="text-[11px] text-amber-200 leading-relaxed">{CUSTOM_CONSULT_NOTICE}</p>}
                {q.items?.length > 0 && q.items.map((it, idx) => (
                  <div key={idx} className="text-xs text-white/60 flex justify-between gap-3">
                    <span>{it.name}{it.scope ? ` · ${it.scope}` : ""}{it.note ? ` · ${it.note}` : ""}</span>
                    <span className="text-white/80 shrink-0">{it.note && String(it.note).includes("포함") && !(it.line_total || ((it.unit_price || it.amount || 0) * (it.quantity || 1))) ? "패키지 포함" : `${((it.unit_price || it.amount || 0) * (it.quantity || 1)).toLocaleString()}원`}</span>
                  </div>
                ))}
                <QuoteTechnicalReview quote={q} onUpdated={load} onOpenQuote={(id) => { setKindFilter("all"); setExpandedId(id); }} />
              </div>
            )}
          </div>
        ))}
      </div>
      <AdminPagination page={pagedQuotes.page} pageSize={20} total={visibleQuotes.length} onPageChange={setPage} />
      {chatRoom && (
        <QuoteChat
          roomId={chatRoom}
          onClose={() => setChatRoom(null)}
          quoteTitle={quotes.find((q) => q.id === chatRoom)?.title}
          customerEmail={quotes.find((q) => q.id === chatRoom)?.customer_email}
          customerName={quotes.find((q) => q.id === chatRoom)?.customer_name}
        />
      )}
      {deleteTarget && (
        <DeleteRequestModal
          resourceType={deleteTarget.type}
          resourceId={deleteTarget.id}
          resourceTitle={deleteTarget.title}
          customerName={deleteTarget.customer}
          onClose={() => setDeleteTarget(null)}
          onDone={() => { setDeleteTarget(null); load(); }}
        />
      )}
      {batchOpen && (
        <BatchDeleteModal
          resourceType="quote"
          ids={selectedIds}
          isOwner={isOwner}
          onClose={() => setBatchOpen(false)}
          onDone={(data) => {
            const gone = new Set([...(data?.deleted || []), ...(data?.requested || [])]);
            setSelectedIds((prev) => prev.filter((id) => !gone.has(id)));
            setBatchOpen(false);
            load();
          }}
        />
      )}
      {quoteToDelete && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="owner-hard-delete-modal">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 w-full max-w-md space-y-3">
            <h4 className="font-medium">이 견적을 정말 삭제하시겠습니까?</h4>
            <p className="text-sm text-white/70">견적명: {quoteToDelete.title || "-"}</p>
            <p className="text-sm text-white/70">고객명: {quoteToDelete.customer_name || quoteToDelete.customer_email || "-"}</p>
            <p className="text-sm text-white/70">총 금액: {(quoteToDelete.total_amount ?? Math.round((quoteToDelete.supply_amount ?? quoteToDelete.total ?? 0) * 1.1)).toLocaleString()}원</p>
            <p className="text-xs text-red-300">삭제한 견적은 복구할 수 없습니다.</p>
            <div className="flex gap-2">
              <button type="button" disabled={hardDeleting} onClick={() => setQuoteToDelete(null)} className="border border-white/10 rounded-lg px-3 py-2 text-sm">취소</button>
              <button type="button" disabled={hardDeleting} onClick={handleHardDeleteQuote} className="bg-red-500 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50" data-testid="owner-hard-delete-submit">
                {hardDeleting ? "삭제 중..." : "삭제 실행"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const EMPTY_CONTRACT_FORM = {
  title: "",
  customer_id: "",
  customer_email: "",
  contract_date: "",
  contract_amount: 0,
  project_scale: "standard",
  party_a_company: "",
  party_a_business_no: "",
  party_a_ceo: "",
  party_a_address: "",
  party_a_phone: "",
  notes: "",
  quote_id: "",
  contract_clause_snapshot: [],
  special_terms_snapshot: [],
};

function ContractsTab({ customers, focusContractId, prefillQuote, onPrefillConsumed }) {
  const { user } = useAuth();
  const isOwner = user?.role === "super_admin";
  const [contracts, setContracts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_CONTRACT_FORM });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [hardDeleting, setHardDeleting] = useState(false);
  const [clausesLocked, setClausesLocked] = useState(false);
  const [clauseSync, setClauseSync] = useState(null);
  const [specialsLocked, setSpecialsLocked] = useState(false);
  const [fallbackClauses, setFallbackClauses] = useState([]);
  const [fallbackSpecials, setFallbackSpecials] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [batchOpen, setBatchOpen] = useState(false);

  const resetForm = () => {
    setForm({ ...EMPTY_CONTRACT_FORM });
    setEditingId(null);
    setShowForm(false);
    setClausesLocked(false);
    setSpecialsLocked(false);
  };

  const load = async () => {
    try {
      const r = await api.get("/admin/contracts");
      setContracts(r.data);
    } catch {
      toast.error("로드 실패");
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!prefillQuote) return;
    let cancelled = false;
    const amount = Number(prefillQuote.total_amount ?? Math.round((prefillQuote.supply_amount ?? prefillQuote.total ?? 0) * 1.1)) || 0;
    (async () => {
      setEditingId(null);
      setClausesLocked(false);
      setSpecialsLocked(false);
      let clauses = [];
      let specials = [];
      try {
        const r = await api.get("/admin/contract-clauses");
        clauses = normalizeClauseRows((r.data.clauses || []).filter((row) => row.enabled !== false));
        specials = normalizeSpecialRows((r.data.specials || []).filter((row) => row.enabled !== false));
        if (!cancelled) {
          setFallbackClauses(normalizeClauseRows(r.data.fallback_clauses || []));
          setFallbackSpecials(normalizeSpecialRows(r.data.fallback_specials || []));
        }
      } catch {
        toast.error("기본 계약 조항을 불러오지 못했습니다.");
      }
      if (cancelled) return;
      setForm({
        ...EMPTY_CONTRACT_FORM,
        title: prefillQuote.title || "",
        customer_id: prefillQuote.customer_id || "",
        customer_email: prefillQuote.customer_email || "",
        contract_amount: amount,
        quote_id: prefillQuote.id || "",
        contract_clause_snapshot: clauses,
        special_terms_snapshot: specials,
      });
      setShowForm(true);
      onPrefillConsumed?.();
    })();
    return () => { cancelled = true; };
  }, [prefillQuote]);

  useEffect(() => {
    if (!focusContractId) return;
    const el = document.querySelector(`[data-testid="admin-contract-${focusContractId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusContractId, contracts]);

  const fillFromCustomer = (customerId) => {
    const c = customers.find((x) => x.id === customerId);
    if (!c) return;
    setForm((prev) => ({
      ...prev,
      customer_id: customerId,
      customer_email: c.email || prev.customer_email,
      party_a_company: c.company || prev.party_a_company,
      party_a_ceo: c.name || prev.party_a_ceo,
      party_a_phone: c.phone || prev.party_a_phone,
    }));
  };

  const openCreate = async () => {
    setEditingId(null);
    setClausesLocked(false);
    setSpecialsLocked(false);
    let clauses = [];
    let specials = [];
    try {
      const r = await api.get("/admin/contract-clauses");
      clauses = normalizeClauseRows((r.data.clauses || []).filter((row) => row.enabled !== false));
      specials = normalizeSpecialRows((r.data.specials || []).filter((row) => row.enabled !== false));
      setFallbackClauses(normalizeClauseRows(r.data.fallback_clauses || []));
      setFallbackSpecials(normalizeSpecialRows(r.data.fallback_specials || []));
    } catch {
      toast.error("기본 계약 조항을 불러오지 못했습니다.");
    }
    setForm({ ...EMPTY_CONTRACT_FORM, contract_clause_snapshot: clauses, special_terms_snapshot: specials });
    setShowForm(true);
  };

  const openEdit = async (c) => {
    setEditingId(c.id);
    const hasClauses = Array.isArray(c.contract_clause_snapshot);
    const hasSpecials = Array.isArray(c.special_terms_snapshot);
    setClausesLocked(!hasClauses);
    setSpecialsLocked(!hasSpecials);
    try {
      const r = await api.get("/admin/contract-clauses");
      setFallbackClauses(normalizeClauseRows(r.data.fallback_clauses || []));
      setFallbackSpecials(normalizeSpecialRows(r.data.fallback_specials || []));
    } catch { /* keep previous fallback */ }
    try {
      const s = await api.get(`/admin/contracts/${c.id}/clause-sync`);
      setClauseSync(s.data);
    } catch { setClauseSync(null); }
    setForm({
      title: c.title || "",
      customer_id: c.customer_id || "",
      customer_email: c.customer_email || "",
      contract_date: toDateInputValue(c.contract_date),
      contract_amount: c.contract_amount || 0,
      project_scale: c.project_scale || "standard",
      party_a_company: c.party_a_company || "",
      party_a_business_no: c.party_a_business_no || "",
      party_a_ceo: c.party_a_ceo || "",
      party_a_address: c.party_a_address || "",
      party_a_phone: c.party_a_phone || "",
      notes: c.notes || "",
      quote_id: c.quote_id || "",
      contract_clause_snapshot: hasClauses ? normalizeClauseRows(c.contract_clause_snapshot) : [],
      special_terms_snapshot: hasSpecials ? normalizeSpecialRows(c.special_terms_snapshot) : [],
    });
    setShowForm(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title) return toast.error("프로젝트명 필수");
    try {
      const payload = {
        title: form.title,
        contract_amount: Number(form.contract_amount) || 0,
        project_scale: form.project_scale || "standard",
        party_a_company: form.party_a_company || "",
        party_a_business_no: form.party_a_business_no || "",
        party_a_ceo: form.party_a_ceo || "",
        party_a_address: form.party_a_address || "",
        party_a_phone: form.party_a_phone || "",
      };
      if (form.customer_id) payload.customer_id = form.customer_id;
      if (String(form.customer_email || "").trim()) payload.customer_email = String(form.customer_email).trim();
      if (form.contract_date) payload.contract_date = form.contract_date;
      if (form.notes) payload.notes = form.notes;
      if (form.quote_id) payload.quote_id = form.quote_id;
      if (form.customer_id) {
        const c = customers.find((x) => x.id === form.customer_id);
        if (c?.email) payload.customer_email = c.email;
      }
      if (editingId && clausesLocked) {
        delete payload.contract_clause_snapshot;
      } else {
        payload.contract_clause_snapshot = normalizeClauseRows(form.contract_clause_snapshot || []);
      }
      if (editingId && specialsLocked) {
        delete payload.special_terms_snapshot;
      } else {
        payload.special_terms_snapshot = normalizeSpecialRows(form.special_terms_snapshot || []);
      }
      if (editingId) {
        await api.patch(`/admin/contracts/${editingId}`, payload);
        toast.success("계약서가 수정되었습니다.");
      } else {
        await api.post("/admin/contracts", payload);
        toast.success("계약서 생성됨");
      }
      resetForm();
      load();
    } catch (e) {
      toast.error(contractCreateErrorMessage(e));
    }
  };

  const sendContract = async (cid, customerEmail) => {
    if (!window.confirm(`고객(${customerEmail || "이메일 확인 필요"})에게 계약서를 발송하시겠습니까?`)) return;
    const loading = toast.loading("계약서 메일 발송 중...");
    try {
      const r = await api.post(`/admin/contracts/${cid}/send`);
      const sentTo = r.data?.sent_to;
      toast.success(sentTo ? `발송 완료: ${sentTo}` : "발송되었습니다.", { id: loading, duration: 6000 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "계약서 발송에 실패했습니다.", { id: loading });
    }
  };

  const copyContract = async (cid) => { await api.post(`/admin/contracts/${cid}/copy`); load(); };

  const handleHardDeleteContract = async () => {
    if (!contractToDelete?.id || hardDeleting) return;
    const contractId = contractToDelete.id;
    setHardDeleting(true);
    try {
      await api.delete(`/admin/contracts/${contractId}/hard`);
      toast.success("계약서가 삭제되었습니다.");
      setContractToDelete(null);
      load();
      refreshAdminNotifications();
    } catch (e) {
      toast.error(contractDeleteErrorMessage(e));
    } finally {
      setHardDeleting(false);
    }
  };

  const downloadPdf = async (cid) => {
    const loading = toast.loading("PDF 준비 중입니다...");
    try {
      const r = await api.get(`/admin/contracts/${cid}/pdf`, {
        params: { t: Date.now() },
        responseType: "blob",
        timeout: 120000,
      });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `contract-${cid.slice(0, 8)}-${Date.now()}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("다운로드합니다.", { id: loading });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF 다운로드 실패", { id: loading });
    }
  };

  const CSTATUS = CONTRACT_STATUS_LABEL;
  const filteredContracts = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (contracts || []).filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (!needle) return true;
      return [row.title, row.customer_email, row.customer_name, row.status].filter(Boolean).join(" ").toLowerCase().includes(needle);
    });
  }, [contracts, q, statusFilter]);
  const pagedContracts = paginateItems(filteredContracts, page, 20);

  return (
    <div className="space-y-4" data-testid="admin-contracts">
      <AdminPageHeader
        title="계약 관리"
        description="서비스 계약서 PDF가 고객 이메일로 발송되며, 관리자 메일에도 사본이 함께 발송됩니다."
        actions={(
          <button onClick={() => (showForm && !editingId ? resetForm() : openCreate())} data-testid="contract-toggle-form" className="bg-white text-black px-4 py-2 rounded-lg text-sm flex items-center gap-1.5 shrink-0"><Plus size={14}/>새 계약서</button>
        )}
      />
      <div className="admin-toolbar">
        <input className="admin-toolbar__search" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="고객명 / 계약명 / 상태 검색" />
        <select className="admin-filter" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">전체 상태</option>
          {Object.entries(CONTRACT_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <span className="admin-count">{filteredContracts.length}건</span>
      </div>
      <SalesBulkBar
        visibleIds={pagedContracts.items.map((row) => row.id)}
        selectedIds={selectedIds}
        onToggleAll={(on) => setSelectedIds(on ? pagedContracts.items.map((row) => row.id) : [])}
        onDelete={() => setBatchOpen(true)}
        deleteLabel={isOwner ? "선택항목 삭제" : "선택항목 삭제 요청"}
        testId="contract-bulk-bar"
      />

      {showForm && (
        <form onSubmit={submit} className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-sm font-medium">{editingId ? "계약서 수정" : "새 계약서 작성"}</h4>
            <button type="button" onClick={resetForm} className="text-white/50 hover:text-white"><X size={16}/></button>
          </div>
          <input placeholder="프로젝트명 *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="contract-title" className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>
          <select
            value={form.customer_id}
            onChange={(e) => { fillFromCustomer(e.target.value); setForm((prev) => ({ ...prev, customer_id: e.target.value })); }}
            className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"
          >
            <option value="">고객 선택 (또는 이메일 직접 입력)</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name} - {c.email}</option>)}
          </select>
          {!form.customer_id && (
            <input placeholder="고객 이메일" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <DateField label="계약 체결일" value={form.contract_date} onChange={(v) => setForm({ ...form, contract_date: v })} />
            <input type="number" placeholder="계약 금액(원)" value={form.contract_amount} onChange={(e) => setForm({ ...form, contract_amount: e.target.value })} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>
            <select value={form.project_scale} onChange={(e) => setForm({ ...form, project_scale: e.target.value })} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm">
              <option value="standard">일반 프로젝트</option>
              <option value="large">대형 프로젝트</option>
            </select>
          </div>
          <div className="text-xs text-white/50 font-medium">갑(고객) 정보 — PDF 서명란에 표시</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input placeholder="상호" value={form.party_a_company} onChange={(e) => setForm({ ...form, party_a_company: e.target.value })} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>
            <input placeholder="사업자등록번호" value={form.party_a_business_no} onChange={(e) => setForm({ ...form, party_a_business_no: e.target.value })} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>
            <input placeholder="대표자" value={form.party_a_ceo} onChange={(e) => setForm({ ...form, party_a_ceo: e.target.value })} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>
            <input placeholder="연락처" value={form.party_a_phone} onChange={(e) => setForm({ ...form, party_a_phone: e.target.value })} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>
            <input placeholder="주소" value={form.party_a_address} onChange={(e) => setForm({ ...form, party_a_address: e.target.value })} className="sm:col-span-2 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>
          </div>
          <div className="space-y-2 border-t border-white/10 pt-3">
            <div className="text-xs text-white/50 font-medium">계약 조항 (이 계약만)</div>
            {clausesLocked ? (
              <div className="space-y-2">
                <p className="text-[11px] text-white/40">기존 계약입니다. 저장해도 조항은 기존 PDF 문구를 유지합니다.</p>
                <div className="text-[11px] text-white/35 space-y-1 max-h-40 overflow-auto">
                  {fallbackClauses.map((row) => (
                    <div key={row.id}>제{row.article_no}조 ({row.title})</div>
                  ))}
                </div>
                <button
                  type="button"
                  className="text-xs border border-white/15 rounded px-3 py-1.5"
                  onClick={() => {
                    setForm({ ...form, contract_clause_snapshot: fallbackClauses.length ? fallbackClauses : [] });
                    setClausesLocked(false);
                  }}
                >
                  이 계약 조항 수정
                </button>
              </div>
            ) : (
              <>
                {clauseSync?.master_updated && clauseSync?.syncable && (
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-amber-300 mb-2">
                    <span>공통 조항이 변경되었습니다.</span>
                    <button
                      type="button"
                      className="text-xs border border-amber-300/40 rounded px-3 py-1.5"
                      onClick={async () => {
                        try {
                          const r = await api.post(`/admin/contracts/${editingId}/sync-clauses`, { mode: "all", include_specials: true });
                          setForm((f) => ({
                            ...f,
                            contract_clause_snapshot: normalizeClauseRows(r.data.contract_clause_snapshot || []),
                            special_terms_snapshot: normalizeSpecialRows(r.data.special_terms_snapshot || f.special_terms_snapshot),
                          }));
                          setClauseSync((s) => ({ ...(s || {}), master_updated: false }));
                          toast.success("최신 조항을 반영했습니다.");
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || "반영 실패");
                        }
                      }}
                    >
                      최신 조항 반영
                    </button>
                  </div>
                )}
                {clauseSync?.frozen && (
                  <p className="text-[11px] text-white/40 mb-2">발송·확정된 계약은 공통 조항이 자동 변경되지 않습니다.</p>
                )}
                <ContractClauseEditor
                rows={form.contract_clause_snapshot || []}
                onChange={(next) => setForm({ ...form, contract_clause_snapshot: next })}
                withEnabled
              />
              </>
            )}
          </div>
          <div className="space-y-2 border-t border-white/10 pt-3">
            <div className="text-xs text-white/50 font-medium">특약사항 (이 계약만)</div>
            {specialsLocked ? (
              <div className="space-y-2">
                <p className="text-[11px] text-white/40">기존 계약입니다. 저장해도 특약은 기존 PDF 문구를 유지합니다.</p>
                <div className="text-[11px] text-white/35 space-y-1 max-h-32 overflow-auto">
                  {fallbackSpecials.map((row) => (
                    <div key={row.id}>{row.text}</div>
                  ))}
                </div>
                <button
                  type="button"
                  className="text-xs border border-white/15 rounded px-3 py-1.5"
                  onClick={() => {
                    setForm({ ...form, special_terms_snapshot: fallbackSpecials.length ? fallbackSpecials : [] });
                    setSpecialsLocked(false);
                  }}
                >
                  이 계약 특약 수정
                </button>
              </div>
            ) : (
              <ContractSpecialEditor
                rows={form.special_terms_snapshot || []}
                onChange={(next) => setForm({ ...form, special_terms_snapshot: next })}
                withEnabled
              />
            )}
          </div>
          <textarea placeholder="추가 특약사항 (선택, PDF 하단 별도 표시)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2 text-sm"/>
          <div className="flex gap-2">
            <button type="submit" data-testid="contract-submit" className="bg-white text-black px-4 py-2 rounded-lg text-sm">{editingId ? "수정 저장" : "생성"}</button>
            <button type="button" onClick={resetForm} className="border border-white/10 px-4 py-2 rounded-lg text-sm text-white/60">취소</button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {filteredContracts.length === 0 && <AdminEmptyState message="아직 생성된 계약이 없습니다." action={<button type="button" className="admin-row__btn" onClick={openCreate}>새 계약서</button>} />}
        {pagedContracts.items.map((c) => (
          <div
            key={c.id}
            data-testid={`admin-contract-${c.id}`}
            className={`bg-[#0a0a0a] border rounded-xl px-4 py-3 ${focusContractId === c.id ? "admin-quote-card--focus" : "border-white/10"}`}
          >
            <div className="flex justify-between items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 shrink-0"
                checked={selectedIds.includes(c.id)}
                onChange={(e) => setSelectedIds((prev) => toggleId(prev, c.id, e.target.checked))}
                data-testid={`contract-check-${c.id}`}
              />
              <div className="min-w-0">
                <div className="font-medium">{c.title}</div>
                <div className="text-xs text-white/40 mt-1">{c.customer_name || c.customer_email || "-"} · {(c.contract_amount || 0).toLocaleString()}원 · {CSTATUS[c.status]} · {c.contract_date || (c.created_at ? new Date(c.created_at).toLocaleDateString("ko-KR") : "")}</div>
                {c.customer_message && <div className="text-xs text-amber-300 mt-2">고객 메시지: {c.customer_message}</div>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button onClick={() => openEdit(c)} className="text-xs border border-white/10 px-3 py-1.5 rounded-full flex items-center gap-1"><Edit3 size={12}/>수정</button>
                <button onClick={() => downloadPdf(c.id)} className="text-xs border border-white/10 px-3 py-1.5 rounded-full flex items-center gap-1"><Download size={12}/>PDF</button>
                <button onClick={() => sendContract(c.id, c.customer_email)} className="text-xs border border-white/10 px-3 py-1.5 rounded-full flex items-center gap-1"><Send size={12}/>발송</button>
                <button onClick={() => copyContract(c.id)} className="text-xs border border-white/10 px-3 py-1.5 rounded-full flex items-center gap-1"><Copy size={12}/>복사</button>
                <button onClick={() => setDeleteTarget({ type: "contract", id: c.id, title: c.title, customer: c.customer_name || c.customer_email })} className="text-xs text-amber-300">
                  {isOwner ? "보관" : "삭제 요청"}
                </button>
                {isOwner && (
                  <button
                    type="button"
                    onClick={() => setContractToDelete(c)}
                    className="text-xs text-red-400 flex items-center gap-1"
                    data-testid={`contract-hard-delete-${c.id}`}
                  >
                    <Trash2 size={12}/>삭제
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <AdminPagination page={pagedContracts.page} pageSize={20} total={filteredContracts.length} onPageChange={setPage} />
      {deleteTarget && (
        <DeleteRequestModal
          resourceType={deleteTarget.type}
          resourceId={deleteTarget.id}
          resourceTitle={deleteTarget.title}
          customerName={deleteTarget.customer}
          onClose={() => setDeleteTarget(null)}
          onDone={() => { setDeleteTarget(null); load(); }}
        />
      )}
      {batchOpen && (
        <BatchDeleteModal
          resourceType="contract"
          ids={selectedIds}
          isOwner={isOwner}
          onClose={() => setBatchOpen(false)}
          onDone={(data) => {
            const gone = new Set([...(data?.deleted || []), ...(data?.requested || [])]);
            setSelectedIds((prev) => prev.filter((id) => !gone.has(id)));
            setBatchOpen(false);
            load();
          }}
        />
      )}
      {contractToDelete && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="contract-hard-delete-modal">
          <div className="bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 w-full max-w-md space-y-3">
            <h4 className="font-medium">이 계약서를 정말 삭제하시겠습니까?</h4>
            <p className="text-sm text-white/70">계약명: {contractToDelete.title || "-"}</p>
            <p className="text-sm text-white/70">고객명: {contractToDelete.customer_name || contractToDelete.customer_email || "-"}</p>
            <p className="text-sm text-white/70">계약금액: {(contractToDelete.contract_amount || 0).toLocaleString()}원</p>
            <p className="text-sm text-white/70">계약상태: {CSTATUS[contractToDelete.status] || contractToDelete.status || "-"}</p>
            {(contractToDelete.document_no || contractToDelete.id) && (
              <p className="text-sm text-white/70">문서번호: {contractToDelete.document_no || contractToDelete.id}</p>
            )}
            <p className="text-xs text-red-300">삭제한 계약서는 복구할 수 없습니다.</p>
            <p className="text-xs text-white/50">연결된 견적 및 고객 정보는 삭제되지 않습니다.</p>
            <div className="flex gap-2">
              <button type="button" disabled={hardDeleting} onClick={() => setContractToDelete(null)} className="border border-white/10 rounded-lg px-3 py-2 text-sm">취소</button>
              <button type="button" disabled={hardDeleting} onClick={handleHardDeleteContract} className="bg-red-500 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-50" data-testid="contract-hard-delete-submit">
                {hardDeleting ? "삭제 중..." : "계약서 삭제"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

